import React, { useEffect, useState } from "react";
import { ArrowLeft, MessageSquareLock, RefreshCw, ShieldCheck, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { api, errMsg } from "../lib/api";

/**
 * Phase 206 — Step 2 of citizen registration: the 6-digit code sent by SMS to the mobile being registered.
 *   muniCode   — tenant code
 *   challenge  — payload of POST /t/{code}/auth/register/send-otp (may carry demo_otp / demo_note when no real SMS goes out)
 *   onVerified — receives { challenge_id, proof, mobile }
 *   onBack     — return to the details form (change the number)
 */
export function CitizenOtpStep({ muniCode, challenge, onVerified, onBack, busyLabel = "Verifying…" }) {
  const [current, setCurrent] = useState(challenge);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [left, setLeft] = useState(challenge?.expires_in || 300);
  const [resendIn, setResendIn] = useState(challenge?.resend_after || 30);

  useEffect(() => { setCurrent(challenge); setOtp(""); setLeft(challenge?.expires_in || 300); setResendIn(challenge?.resend_after || 30); }, [challenge]);
  useEffect(() => {
    const t = setInterval(() => { setLeft((s) => Math.max(0, s - 1)); setResendIn((s) => Math.max(0, s - 1)); }, 1000);
    return () => clearInterval(t);
  }, []);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const verify = async (code) => {
    const value = (code ?? otp).trim();
    if (value.length !== 6 || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${muniCode}/auth/register/verify-otp`, { challenge_id: current.challenge_id, otp: value });
      await onVerified({ challenge_id: current.challenge_id, proof: r.data.proof, mobile: r.data.mobile });
    } catch (err) {
      toast.error(errMsg(err));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true);
    try {
      const r = await api.post(`/t/${muniCode}/auth/register/resend-otp`, { challenge_id: current.challenge_id });
      setCurrent(r.data);
      setOtp("");
      setLeft(r.data.expires_in || 300);
      setResendIn(r.data.resend_after || 30);
      toast.success(r.data.demo ? "A new code was generated" : "A new code has been sent by SMS");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-4 mt-2" data-testid="citizen-otp-step">
      <div className="flex items-start gap-2.5 text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2.5">
        <MessageSquareLock className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <div>
          <p className="font-medium text-foreground">Verify your mobile number</p>
          <p>
            {current?.demo ? "A 6-digit code was generated for " : "We sent a 6-digit code by SMS to "}
            <span className="font-mono text-foreground" data-testid="citizen-otp-mobile-hint">{current?.mobile_hint}</span>.
            {" "}It expires in <span className="tabular-nums" data-testid="citizen-otp-expiry">{mmss(left)}</span>.
          </p>
        </div>
      </div>

      {current?.demo && current?.demo_otp && (
        <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" data-testid="citizen-otp-demo-alert">
          <FlaskConical className="h-4 w-4" aria-hidden="true" />
          <AlertTitle className="text-sm">Your code: <span className="font-mono tracking-[0.3em] text-base" data-testid="citizen-otp-demo-code">{current.demo_otp}</span></AlertTitle>
          <AlertDescription className="text-xs" data-testid="citizen-otp-demo-note">{current.demo_note}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col items-center gap-3" data-testid="citizen-otp-input-wrap">
        <InputOTP maxLength={6} value={otp} onChange={(v) => { setOtp(v); if (v.length === 6) verify(v); }} disabled={busy || left === 0}
          inputMode="numeric" autoFocus data-testid="citizen-otp-input" aria-label="6-digit verification code">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-11 w-10 text-lg" data-testid={`citizen-otp-slot-${i}`} />)}
          </InputOTPGroup>
        </InputOTP>
        {left === 0 && <p className="text-xs text-destructive" data-testid="citizen-otp-expired">This code has expired — request a new one.</p>}
      </div>

      <Button className="w-full" onClick={() => verify()} disabled={busy || otp.length !== 6 || left === 0} data-testid="citizen-otp-submit-button">
        <ShieldCheck className="h-4 w-4 mr-2" aria-hidden="true" /> {busy ? busyLabel : "Verify & create account"}
      </Button>

      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="citizen-otp-back-button">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Change number
        </button>
        <button type="button" onClick={resend} disabled={resendIn > 0 || resending}
          className="inline-flex items-center gap-1 text-primary disabled:text-muted-foreground disabled:cursor-not-allowed" data-testid="citizen-otp-resend-button">
          <RefreshCw className={`h-3.5 w-3.5 ${resending ? "animate-spin" : ""}`} aria-hidden="true" />
          {resending ? "Sending…" : resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
        </button>
      </div>
    </div>
  );
}
